import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { JwtHelperService } from "@auth0/angular-jwt";
import { Router } from "@angular/router";
import * as jwtDecode from "jwt-decode";

import { environment } from "@environments/environment";
import { UserRoleEnum } from "../enums/UserRoleEnum";
import { IResultHttp } from "../interface/IResultHttp";

@Injectable({ providedIn: "root" })
export class AuthService {
  private readonly ACCESS_TOKEN = "ACCESS_TOKEN";
  private readonly REFRESH_TOKEN = "REFRESH_TOKEN";
  private readonly ID_TOKEN = "ID_TOKEN";
  private readonly REDIRECT_PATH = "KEYCLOAK_REDIRECT_PATH";
  private keycloak: any;
  private keycloakInitPromise: Promise<boolean>;

  constructor(
    public http: HttpClient,
    public jwtHelperService: JwtHelperService,
    public router: Router
  ) {}

  async signIn(username?: string, password?: string): Promise<boolean> {
    await this.loginWithKeycloak();
    return false;
  }

  async refresh() {
    try {
      await this.initKeycloak();

      if (this.keycloak && this.keycloak.authenticated) {
        const refreshed = await this.keycloak.updateToken(60);
        if (refreshed || this.keycloak.token) {
          this.saveKeycloakTokens();
          return true;
        }
      }

      return false;
    } catch (err) {
      console.error(err);
    }

    this.clearTokens();
    return false;
  }

  async signOut() {
    const refreshToken = this.getRefreshToken();
    this.clearTokens();

    if (refreshToken) {
      try {
        const body = new URLSearchParams();
        body.set("client_id", environment.keycloakClientId);
        body.set("client_secret", environment.keycloakClientSecret);
        body.set("refresh_token", refreshToken);

        const headers = new HttpHeaders({
          "x-www-form-urlencoded": "true",
          "Skip-Load": "true"
        });
        const url = `${environment.keycloakUrl}/realms/${environment.keycloakRealm}/protocol/openid-connect/logout`;

        await this.http.post<any>(url, body.toString(), { headers }).toPromise();
      } catch (err) {
        console.error(err);
      }
    }

    this.router.navigate(["/login"]);
  }

  saveToken(token: string, value: string) {
    return localStorage.setItem(token, value);
  }

  clearTokens() {
    localStorage.removeItem(this.ACCESS_TOKEN);
    localStorage.removeItem(this.REFRESH_TOKEN);
    localStorage.removeItem(this.ID_TOKEN);
    localStorage.removeItem(this.REDIRECT_PATH);
  }

  getAccessToken() {
    return localStorage.getItem(this.ACCESS_TOKEN);
  }

  getRefreshToken() {
    return localStorage.getItem(this.REFRESH_TOKEN);
  }

  getTokenPayload() {
    const accessToken = this.getAccessToken();
    if (!accessToken) {
      return {};
    }

    try {
      return jwtDecode(accessToken);
    } catch (err) {
      this.clearTokens();
      return {};
    }
  }

  getJwtTokenPayload() {
    const jwtToken = sessionStorage.getItem("JWT_TOKEN");
    if (!jwtToken) {
      return {};
    }

    try {
      return jwtDecode(jwtToken);
    } catch (err) {
      sessionStorage.removeItem("JWT_TOKEN");
      return {};
    }
  }

  getTokenRoles(): UserRoleEnum[] {
    const auditorRole = "SIS_CAPTURE_AUDITOR";
    const organizationManagerRole = "captureweb-externo";
    const adminRole = "SIS_CAPTURE_ADMINISTRADOR";
    const consultaRole = "GRP_CEI_SUPORTE";
    const masterRole = "SIS_CAPTURE_MASTER";
    const { realm_access, resource_access } = this.getTokenPayload();
    let roles = (realm_access && realm_access.roles) || [];
    if (
      resource_access &&
      resource_access.captureweb &&
      resource_access.captureweb.roles
    ) {
      roles = [...roles, ...resource_access.captureweb.roles];
    }
    const genericRoles: UserRoleEnum[] = [];
    if (roles.length < 1) {
      return genericRoles;
    }
    if (roles.includes(auditorRole)) {
      genericRoles.push(UserRoleEnum.AUDITOR);
    }
    if (roles.includes(organizationManagerRole)) {
      genericRoles.push(UserRoleEnum.MANAGER);
    }
    if (roles.includes(adminRole)) {
      genericRoles.push(UserRoleEnum.ADMIN);
    }
    if (roles.includes(consultaRole)) {
      genericRoles.push(UserRoleEnum.GRP_CEI_SUPORTE);
    }
    if (roles.includes(masterRole)) {
      genericRoles.push(UserRoleEnum.MASTER);
    }
    return genericRoles;
  }

  async isAuthenticated() {
    const authenticatedByKeycloak = await this.initKeycloak();
    if (authenticatedByKeycloak) {
      this.saveKeycloakTokens();
      return true;
    }

    const accessToken = this.getAccessToken();

    if (accessToken) {
      let notExpired = false;
      try {
        notExpired = !this.jwtHelperService.isTokenExpired(accessToken);
      } catch (err) {
        this.clearTokens();
      }

      if (notExpired) {
        return true;
      }

      const refreshToken = this.getRefreshToken();
      let notExpiredRefreshToken = false;
      try {
        notExpiredRefreshToken = refreshToken && !this.jwtHelperService.isTokenExpired(refreshToken);
      } catch (err) {
        this.clearTokens();
      }
      if (notExpiredRefreshToken) {
        if (await this.refresh()) {
          return true;
        }
      }
    }

    return false;
  }

  async loginWithKeycloak() {
    const authenticated = await this.initKeycloak();
    if (authenticated) {
      this.saveKeycloakTokens();
      this.router.navigate(["/dashboard"]);
      return true;
    }

    localStorage.setItem(this.REDIRECT_PATH, "/dashboard");
    return this.keycloak.login({
      redirectUri: this.getBaseRedirectUri()
    });
  }

  async initializeFromKeycloak() {
    try {
      if (this.clearLoginRequiredCallback()) {
        return;
      }

      if (await this.exchangeAuthorizationCode()) {
        this.navigateAfterKeycloakLogin();
        return;
      }

      const authenticated = await this.initKeycloak();
      if (authenticated) {
        this.saveKeycloakTokens();
        this.navigateAfterKeycloakLogin();
      }
    } catch (err) {
      console.error(err);
    }
  }

  private async initKeycloak(): Promise<boolean> {
    if (this.clearLoginRequiredCallback()) {
      return false;
    }

    if (this.keycloakInitPromise) {
      return this.keycloakInitPromise;
    }

    this.keycloakInitPromise = this.loadKeycloakAdapter()
      .then(() => {
        if (!this.keycloak) {
          this.keycloak = new (window as any).Keycloak({
            url: environment.keycloakUrl,
            realm: environment.keycloakRealm,
            clientId: environment.keycloakClientId
          });
        }

        return this.keycloak.init({
          checkLoginIframe: false,
          responseMode: "query"
        });
      })
      .catch(err => {
        this.keycloakInitPromise = null;
        throw err;
      });

    return this.keycloakInitPromise;
  }

  private loadKeycloakAdapter(): Promise<void> {
    if ((window as any).Keycloak) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = `${environment.keycloakUrl}/js/keycloak.js`;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error("Não foi possível carregar o adapter do Keycloak"));
      document.body.appendChild(script);
    });
  }

  private saveKeycloakTokens() {
    if (this.keycloak && this.keycloak.token) {
      this.saveToken(this.ACCESS_TOKEN, this.keycloak.token);
    }
    if (this.keycloak && this.keycloak.refreshToken) {
      this.saveToken(this.REFRESH_TOKEN, this.keycloak.refreshToken);
    }
    if (this.keycloak && this.keycloak.idToken) {
      this.saveToken(this.ID_TOKEN, this.keycloak.idToken);
    }
  }

  private getHashRedirectUri(path: string) {
    return `${window.location.origin}${window.location.pathname}#${path}`;
  }

  private getBaseRedirectUri() {
    return `${window.location.origin}${window.location.pathname}`;
  }

  private clearLoginRequiredCallback() {
    if (window.location.hash && window.location.hash.indexOf("error=login_required") >= 0) {
      this.clearTokens();
      window.history.replaceState({}, document.title, this.getHashRedirectUri("/login"));
      return true;
    }

    return false;
  }

  private navigateAfterKeycloakLogin() {
    const redirectPath = localStorage.getItem(this.REDIRECT_PATH);
    if (!redirectPath) {
      return;
    }

    localStorage.removeItem(this.REDIRECT_PATH);
    this.router.navigate([redirectPath]);
  }

  private async exchangeAuthorizationCode() {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    if (!code) {
      return false;
    }

    const body = new URLSearchParams();
    body.set("grant_type", "authorization_code");
    body.set("client_id", environment.keycloakClientId);
    body.set("client_secret", environment.keycloakClientSecret);
    body.set("code", code);
    body.set("redirect_uri", this.getBaseRedirectUri());

    const headers = new HttpHeaders({
      "x-www-form-urlencoded": "true",
    });
    const url = `${environment.keycloakUrl}/realms/${environment.keycloakRealm}/protocol/openid-connect/token`;
    const response = await this.http.post<any>(url, body.toString(), { headers }).toPromise();
    const data = response && response.data ? response.data : response;

    if (data && data.access_token) {
      this.saveToken(this.ACCESS_TOKEN, data.access_token);
      if (data.refresh_token) {
        this.saveToken(this.REFRESH_TOKEN, data.refresh_token);
      }
      if (data.id_token) {
        this.saveToken(this.ID_TOKEN, data.id_token);
      }
      window.history.replaceState({}, document.title, this.getBaseRedirectUri());
      return true;
    }

    return false;
  }

}
